import React, { CSSProperties, useEffect, useState } from 'react';

import { Check, Search } from 'baseui/icon';
import { useParams } from 'react-router-dom';

import { Button } from 'components/Button';
import { queryClient } from 'config/queryClient';
import { DefectPhoto } from 'services/api/inspection/types';
import { useStitches } from 'theme';

import ModalImageZoom from 'features/Inspection/components/DefectsImageGallery/DefectImageModal';

import { DEFECT_IMAGE_TYPE, UpdateImageParams } from './defectImageTypes';
import {
  eventToPointPercentage,
  getArrowStyle,
  getRectangleStyle,
  getRectangleTLBR,
} from './imageGeometry';
import { useUpdateImage } from './mutations';

export default function InteractiveImage({
  rectangles,
  arrows,
  children,
  params,
  editable = false,
  image,
}: {
  rectangles?: number[][];
  arrows?: number[][];
  children: React.ReactNode;
  params: Omit<UpdateImageParams, 'inspectionId'>;
  editable?: boolean;
  image: DefectPhoto & {
    defectImageType: keyof typeof DEFECT_IMAGE_TYPE;
  };
}) {
  const [isOpen, setIsOpen] = useState(false);
  const mutation = useUpdateImage();
  const { inspectionId = '' } = useParams<{ inspectionId: string }>();
  const [state, setState] = useState<{
    rectangles: number[][];
    arrows: number[][];
    draftRectangle: number[];
    draftArrow: number[];
    initialPoint: number[];
    mouseDown: boolean;
    annotationMode: 'rect' | 'arrow';
  }>({
    rectangles: [],
    arrows: [],
    draftRectangle: [],
    draftArrow: [],
    initialPoint: [],
    mouseDown: false,
    annotationMode: 'rect',
  });

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      rectangles: rectangles ?? [],
      arrows: arrows ?? [],
      draftRectangle: [],
      draftArrow: [],
    }));
  }, [rectangles, arrows]);

  const onMouseDown = (event: any) => {
    if (editable && event.target.tagName === 'IMG') {
      const point = eventToPointPercentage(event);
      setState((prev) => ({
        ...prev,
        mouseDown: true,
        draftRectangle: [point[0], point[1], point[0], point[1]],
        draftArrow: [point[0], point[1], point[0], point[1]],
        initialPoint: [point[0], point[1]],
      }));
    }
  };

  const onMouseMove = (event: any) => {
    if (editable && state.mouseDown && event.target.tagName === 'IMG') {
      const point = eventToPointPercentage(event);
      const topLeftX = Math.min(state.initialPoint[0], point[0]);
      const topLeftY = Math.min(state.initialPoint[1], point[1]);
      const bottomRightX = Math.max(state.initialPoint[0], point[0]);
      const bottomRightY = Math.max(state.initialPoint[1], point[1]);
      setState((prev) => ({
        ...prev,
        draftRectangle: [topLeftX, topLeftY, bottomRightX, bottomRightY],
        draftArrow: [state.initialPoint[0], state.initialPoint[1], point[0], point[1]],
      }));
    }
  };


  const onMouseUp = (event: any) => {
    setState((prev) => ({ ...prev, mouseDown: false }));
  };
  const { css } = useStitches();
  return (
    <div
      className={css({
        height: '$full',
        position: 'relative',
        backgroundColor: '#ecf0f1',
        layout: 'stack',
        alignItems: 'center',
      })}
      draggable={false}
    >
      <div
        role="presentation"
        className={css({
          height: '85%',
          position: 'relative',
        })}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        draggable={false}
      >
        {children}
        {state.rectangles.map((rect, index) => (
          <div
            key={`rect-${index}`}
            style={getRectangleStyle(rect) as CSSProperties}
          />
        ))}
        {state.annotationMode === 'rect' && (
          <div style={getRectangleStyle(state.draftRectangle) as CSSProperties} />
        )}
        <svg
          className={css({
            position: 'absolute',
            inset: 0,
            width: '$full',
            height: '$full',
            pointerEvents: 'none',
            zIndex: 100,
          })}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <marker
              id={`arrowhead-${image.uuid}`}
              markerWidth="6"
              markerHeight="6"
              refX="5"
              refY="3"
              orient="auto"
            >
              <path d="M0,0 L0,6 L6,3 z" fill="#00ff00" />
            </marker>
          </defs>
          {state.arrows.map((arrow, index) => {
            const line = getArrowStyle(arrow);
            if (!line) {
              return null;
            }
            return (
              <line
                key={`arrow-${index}`}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke="#00ff00"
                strokeWidth="0.6"
                markerEnd={`url(#arrowhead-${image.uuid})`}
              />
            );
          })}
          {state.annotationMode === 'arrow' && (() => {
            const draft = getArrowStyle(state.draftArrow);
            if (!draft) {
              return null;
            }
            return (
              <line
                x1={draft.x1}
                y1={draft.y1}
                x2={draft.x2}
                y2={draft.y2}
                stroke="#00ff00"
                strokeWidth="0.6"
                markerEnd={`url(#arrowhead-${image.uuid})`}
              />
            );
          })()}
        </svg>
      </div>

      <nav
        className={css({
          display: 'flex',
          gap: '20px',
          height: '15%',
          alignItems: 'center',

        })}
      >
        {editable && (
          <>
            <Button
              size="compact"
              onClick={() => {
                setState((prev) => ({
                  ...prev,
                  annotationMode: 'rect',
                  draftArrow: [],
                }));
              }}
              className={css({
                backgroundColor:
                  state.annotationMode === 'rect'
                    ? '$primaryA'
                    : '$mono200',
              })}
            >
              Rectangle
            </Button>
            <Button
              size="compact"
              onClick={() => {
                setState((prev) => ({
                  ...prev,
                  annotationMode: 'arrow',
                  draftRectangle: [],
                }));
              }}
              className={css({
                backgroundColor:
                  state.annotationMode === 'arrow'
                    ? '$primaryA'
                    : '$mono200',
              })}
            >
              Arrow
            </Button>
          <Button
            startEnhancer={Check}
            size="compact"
            isLoading={mutation.isPending}
            onClick={() => {
              const normalizedDraftRect = getRectangleTLBR(state.draftRectangle);
              const hasDraftRect = normalizedDraftRect.length === 4;
              const hasDraftArrow = state.draftArrow.length === 4;
              const nextRectangles =
                state.annotationMode === 'rect' && hasDraftRect
                  ? [...state.rectangles, normalizedDraftRect]
                  : state.rectangles;
              const nextArrows =
                state.annotationMode === 'arrow' && hasDraftArrow
                  ? [...state.arrows, state.draftArrow]
                  : state.arrows;

              if (nextRectangles.length === 0 && nextArrows.length === 0) {
                return;
              }

              const serializedRectangles = nextRectangles
                .map((rect) => `Rect(${rect.join()})`);
              const serializedArrows = nextArrows
                .map((arrow) => `arrow(${arrow.join()})`);

              const drawables = [...serializedRectangles, ...serializedArrows]
                .join(', ');

              mutation.mutate(
                {
                  ...params,
                  inspectionId,
                  drawables,
                },
                {
                  async onSuccess(data, variables, context) {
                    await queryClient.invalidateQueries({
                      queryKey: [
                        'inspection',
                        variables.inspectionId,
                        'related-cloned-inspections',
                      ],
                    });
                    setState((prev) => ({
                      ...prev,
                      rectangles: nextRectangles,
                      arrows: nextArrows,
                      draftRectangle: [],
                      draftArrow: [],
                    }));
                  },
                },
              );
            }}
          >
            {state.rectangles.length || state.arrows.length
              ? 'Add Annotation'
              : 'Apply Annotation'}
          </Button>
          </>
        )}
        {/* Create button to open the new modal with the image filling in all modal */}
        <ModalImageZoom
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          image={image}
        />
        <Button
          onClick={() => { setIsOpen(true) }}
          size="compact"
          startEnhancer={Search}
        >
          Zoom Image
        </Button>
      </nav>
    </div>
  );
}
